import { useMemo } from 'react'

import { styled } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { Moment } from 'moment'

import { TTime } from '~/core/utils/date'
import type { Days, MilliSeconds } from '~/shared/types/base/units'

interface IDateRangeProps {
  time: TTime
  limitDays?: Days // 限制可选起止时间的范围（单位：天）
  onTimeRangeChange: (time: TTime) => void
}

const Container = styled('div')`
  display: flex;
  align-items: center;
  gap: 1rem;
`

export const DateRange = (props: IDateRangeProps) => {
  const { time, limitDays, onTimeRangeChange } = props

  const startDate = moment(time.startTime)
  const endDate = moment(time.endTime)

  const minStartDateTime = useMemo(() => {
    if (!limitDays) {
      return null
    }

    return moment(time.endTime).subtract(limitDays - 1, 'd')
  }, [time, limitDays])

  const maxStartDateTime = useMemo(() => moment(Math.min(time.lastAvailableDateTime, time.endTime)), [time])

  const maxEndDateTime = useMemo(() => {
    const { startTime, lastAvailableDateTime } = time

    if (!limitDays) {
      return moment(lastAvailableDateTime)
    }

    return moment(
      Math.min(
        lastAvailableDateTime,
        moment(startTime)
          .add(limitDays - 1, 'd')
          .valueOf()
      )
    )
  }, [time, limitDays])

  const timeChange = (st: Moment, et: Moment) => {
    onTimeRangeChange({ ...time, startTime: st.valueOf() as MilliSeconds, endTime: et.valueOf() as MilliSeconds })
  }

  return (
    <Container>
      <span>时间</span>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="zh-cn">
        <DatePicker
          label="选择开始日期"
          minDate={minStartDateTime}
          maxDate={maxStartDateTime}
          value={startDate}
          onChange={(value) => value && timeChange(value, endDate)}
          // 覆盖周天的文案
          // 参考 Issue：https://github.com/mui/mui-x/issues/5222
          // 参考文档：https://mui.com/x/api/date-pickers/date-picker/#DatePicker-prop-dayOfWeekFormatter
          dayOfWeekFormatter={(dayOfWeek: string) => {
            if (!['周一', '周二', '周三', '周四', '周五', '周六', '周日'].includes(dayOfWeek)) {
              return dayOfWeek
            }

            return dayOfWeek.replace('周', '')
          }}
        />
      </LocalizationProvider>
      <span>至</span>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="zh-cn">
        <DatePicker
          label="选择结束日期"
          minDate={startDate}
          maxDate={maxEndDateTime}
          value={endDate}
          onChange={(value) => value && timeChange(startDate, value)}
          // 覆盖周天的文案
          // 参考 Issue：https://github.com/mui/mui-x/issues/5222
          // 参考文档：https://mui.com/x/api/date-pickers/date-picker/#DatePicker-prop-dayOfWeekFormatter
          dayOfWeekFormatter={(dayOfWeek: string) => {
            if (!['周一', '周二', '周三', '周四', '周五', '周六', '周日'].includes(dayOfWeek)) {
              return dayOfWeek
            }

            return dayOfWeek.replace('周', '')
          }}
        />
      </LocalizationProvider>
    </Container>
  )
}
